import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { storage } from '../../data/localStorage';
import GeneratedPaper from './GeneratedPaper';
import QuickGen from './QuickGen';

const { FiFileText, FiUpload, FiRefreshCw, FiImage, FiClock, FiBookOpen, FiAlignLeft, FiAlignCenter, FiAlignRight, FiGrid, FiZap } = FiIcons;

const PaperGenerator = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [generatedPaper, setGeneratedPaper] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showQuickGenModal, setShowQuickGenModal] = useState(false);

  // Criteria-based generation
  const [criteriaForm, setCriteriaForm] = useState({
    title: '',
    subject: '',
    courseCode: '',
    totalMarks: 100,
    duration: 180, // minutes
    sections: [
      {
        id: 1,
        name: 'Section A',
        questionType: 'Multiple Choice',
        count: 10,
        marksPerQuestion: 2,
        difficulty: 'Easy'
      }
    ],
    // Additional paper configuration
    logo: '',
    headerText: '',
    footerText: '',
    showPageNumbers: true,
    pageNumberPosition: 'bottom-right',
    headerAlignment: 'center',
    footerAlignment: 'center',
    fontSize: 'medium',
  });

  const questions = storage.getQuestions();
  const subjects = [...new Set(questions.map(q => q.subject).filter(Boolean))];
  const courseCodes = [...new Set(questions.map(q => q.courseCode).filter(Boolean))];
  const questionTypes = [...new Set(questions.map(q => q.type).filter(Boolean))];
  const difficulties = ['Easy', 'Medium', 'Hard'];

  const addSection = () => {
    const newSectionId = criteriaForm.sections.length > 0 
      ? Math.max(...criteriaForm.sections.map(s => s.id)) + 1 
      : 1;
    
    setCriteriaForm({
      ...criteriaForm,
      sections: [
        ...criteriaForm.sections,
        {
          id: newSectionId,
          name: `Section ${String.fromCharCode(65 + criteriaForm.sections.length)}`,
          questionType: questionTypes[0] || 'Multiple Choice',
          count: 5,
          marksPerQuestion: 2,
          difficulty: 'Medium'
        }
      ]
    });
  };

  const removeSection = (id) => {
    setCriteriaForm({
      ...criteriaForm,
      sections: criteriaForm.sections.filter(s => s.id !== id)
    });
  };

  const updateSection = (id, field, value) => {
    setCriteriaForm({
      ...criteriaForm,
      sections: criteriaForm.sections.map(section => {
        if (section.id === id) {
          return { ...section, [field]: value };
        }
        return section;
      })
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCriteriaForm({...criteriaForm, logo: reader.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const handleQuickGenSubmit = (formData) => {
    setIsLoading(true);
    
    // Simulate API delay
    setTimeout(() => {
      const selectedQuestions = [];
      let actualTotalMarks = 0;
      const questionCount = Math.min(10, questions.length); // Get up to 10 questions
      
      const shuffled = [...questions].sort(() => 0.5 - Math.random());
      const selectedQs = shuffled.slice(0, questionCount).map(q => ({
        ...q,
        assignedMarks: Math.round(formData.totalMarks / questionCount)
      }));
      
      selectedQuestions.push(...selectedQs);
      actualTotalMarks = selectedQs.length * Math.round(formData.totalMarks / questionCount);
      
      const paper = {
        title: formData.title || 'Quick Generated Paper',
        subject: formData.subject || 'General',
        courseCode: formData.courseCode || '',
        duration: formData.duration,
        totalMarks: actualTotalMarks,
        questions: selectedQuestions,
        generationMethod: 'quick-gen',
        generatedAt: new Date().toISOString(),
      };
      
      // Save to storage
      storage.addPaper(paper);
      setGeneratedPaper(paper);
      setIsLoading(false);
    }, 1500);
  };

  const generatePaperByCriteria = async () => {
    setIsLoading(true);
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      const selectedQuestions = [];
      let actualTotalMarks = 0;

      for (const section of criteriaForm.sections) {
        const availableQuestions = questions.filter(q => 
          q.type === section.questionType && 
          q.difficulty === section.difficulty &&
          (!criteriaForm.subject || q.subject === criteriaForm.subject) &&
          (!criteriaForm.courseCode || q.courseCode === criteriaForm.courseCode)
        );

        const shuffled = [...availableQuestions].sort(() => 0.5 - Math.random());
        const sectionQuestions = shuffled.slice(0, section.count).map(q => ({
          ...q,
          sectionName: section.name,
          assignedMarks: section.marksPerQuestion
        }));

        selectedQuestions.push(...sectionQuestions);
        actualTotalMarks += sectionQuestions.length * section.marksPerQuestion;
      }

      const paper = {
        title: criteriaForm.title || 'Generated Exam Paper',
        subject: criteriaForm.subject || 'General',
        courseCode: criteriaForm.courseCode || '',
        duration: criteriaForm.duration,
        totalMarks: actualTotalMarks,
        sections: criteriaForm.sections,
        questions: selectedQuestions,
        generatedAt: new Date().toISOString(),
        // Additional paper configuration
        logo: criteriaForm.logo,
        headerText: criteriaForm.headerText,
        footerText: criteriaForm.footerText,
        showPageNumbers: criteriaForm.showPageNumbers,
        pageNumberPosition: criteriaForm.pageNumberPosition,
        headerAlignment: criteriaForm.headerAlignment,
        footerAlignment: criteriaForm.footerAlignment,
        fontSize: criteriaForm.fontSize,
      };

      // Save to storage
      storage.addPaper(paper);
      setGeneratedPaper(paper);

    } catch (error) {
      console.error('Error generating paper:', error);
      alert(`Error generating paper: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (generatedPaper) {
    return (
      <GeneratedPaper 
        paper={generatedPaper} 
        onBack={() => setGeneratedPaper(null)} 
      />
    );
  }

  return (
    <div className="min-h-screen">
      <div className="space-y-6 p-8 ml-72">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Generate Exam Paper</h2>
            <p className="text-gray-600 dark:text-gray-300 mt-1">Create exam papers using criteria or templates</p>
          </div>
          
          {/* Quick Generate Button */}
          <button
            onClick={() => setShowQuickGenModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
          >
            <SafeIcon icon={FiZap} />
            <span>Quick Generate</span>
          </button>
        </div>

        {/* Basic Form */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <form className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Paper Title
                </label>
                <input
                  type="text"
                  value={criteriaForm.title}
                  onChange={(e) => setCriteriaForm({...criteriaForm, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Mid-term Mathematics Exam"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subject (Optional)
                </label>
                <select
                  value={criteriaForm.subject}
                  onChange={(e) => setCriteriaForm({...criteriaForm, subject: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">All Subjects</option>
                  {subjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Course Code
                </label>
                <select
                  value={criteriaForm.courseCode}
                  onChange={(e) => setCriteriaForm({...criteriaForm, courseCode: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select Course Code</option>
                  {courseCodes.map(code => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  value={criteriaForm.duration}
                  onChange={(e) => setCriteriaForm({...criteriaForm, duration: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  min="30"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Total Marks (Target)
                </label>
                <input
                  type="number"
                  value={criteriaForm.totalMarks}
                  onChange={(e) => setCriteriaForm({...criteriaForm, totalMarks: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  min="1"
                />
              </div>
            </div>

            {/* Sections */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium text-gray-800 dark:text-white">Paper Sections</h3>
                <button
                  type="button"
                  onClick={addSection}
                  className="px-3 py-1 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-800/30 transition-colors text-sm flex items-center"
                >
                  <SafeIcon icon={FiGrid} className="mr-1" /> Add Section
                </button>
              </div>

              <div className="space-y-4">
                {criteriaForm.sections.map((section, index) => (
                  <div key={section.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center">
                        <span className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 flex items-center justify-center text-sm font-medium">
                          {String.fromCharCode(65 + index)}
                        </span>
                        <input
                          type="text"
                          value={section.name}
                          onChange={(e) => updateSection(section.id, 'name', e.target.value)}
                          className="ml-2 px-2 py-1 border-b border-gray-200 dark:border-gray-600 bg-transparent focus:border-primary-500 focus:outline-none text-gray-800 dark:text-white"
                          placeholder="Section Name"
                        />
                      </div>
                      {criteriaForm.sections.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSection(section.id)}
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Question Type</label>
                        <select
                          value={section.questionType}
                          onChange={(e) => updateSection(section.id, 'questionType', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg"
                        >
                          {questionTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Number of Questions</label>
                        <input
                          type="number"
                          min="1"
                          value={section.count}
                          onChange={(e) => updateSection(section.id, 'count', parseInt(e.target.value))}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Marks per Question</label>
                        <input
                          type="number"
                          min="1"
                          value={section.marksPerQuestion}
                          onChange={(e) => updateSection(section.id, 'marksPerQuestion', parseInt(e.target.value))}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Difficulty</label>
                        <select
                          value={section.difficulty}
                          onChange={(e) => updateSection(section.id, 'difficulty', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg"
                        >
                          {difficulties.map(diff => (
                            <option key={diff} value={diff}>{diff}</option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Total Section Marks</label>
                        <div className="py-1 px-2 bg-gray-100 dark:bg-gray-600 rounded-lg text-sm font-medium text-gray-800 dark:text-white">
                          {section.count * section.marksPerQuestion} marks
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Advanced Options Toggle */}
            <div className="pt-4">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium flex items-center"
              >
                {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
                <span className={`ml-1 transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`}>▼</span>
              </button>
            </div>

            {/* Advanced Options */}
            {showAdvanced && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="pt-4 border-t border-gray-200 dark:border-gray-700"
              >
                <h3 className="font-medium text-gray-800 dark:text-white mb-4">Paper Formatting</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Logo Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Institution Logo
                    </label>
                    <div className="flex items-start space-x-4">
                      <div className="flex-1">
                        <label className="cursor-pointer flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <SafeIcon icon={FiImage} className="text-gray-400 text-xl mb-2" />
                            <p className="text-xs text-gray-500 dark:text-gray-400">Click to upload logo</p>
                          </div>
                          <input 
                            type="file" 
                            className="hidden" 
                            accept="image/*"
                            onChange={handleLogoChange}
                          />
                        </label>
                      </div>
                      {criteriaForm.logo && (
                        <div className="w-24 h-24 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                          <img 
                            src={criteriaForm.logo} 
                            alt="Logo Preview" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Header Text */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Header Text
                    </label>
                    <input
                      type="text"
                      value={criteriaForm.headerText}
                      onChange={(e) => setCriteriaForm({...criteriaForm, headerText: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg"
                      placeholder="e.g., Department of Computer Science"
                    />
                    <div className="flex items-center space-x-4 mt-2">
                      <label className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                        <input 
                          type="radio" 
                          checked={criteriaForm.headerAlignment === 'left'}
                          onChange={() => setCriteriaForm({...criteriaForm, headerAlignment: 'left'})}
                          className="text-primary-600"
                        />
                        <SafeIcon icon={FiAlignLeft} />
                        <span>Left</span>
                      </label>
                      <label className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                        <input 
                          type="radio" 
                          checked={criteriaForm.headerAlignment === 'center'}
                          onChange={() => setCriteriaForm({...criteriaForm, headerAlignment: 'center'})}
                          className="text-primary-600"
                        />
                        <SafeIcon icon={FiAlignCenter} />
                        <span>Center</span>
                      </label>
                      <label className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                        <input 
                          type="radio" 
                          checked={criteriaForm.headerAlignment === 'right'}
                          onChange={() => setCriteriaForm({...criteriaForm, headerAlignment: 'right'})}
                          className="text-primary-600"
                        />
                        <SafeIcon icon={FiAlignRight} />
                        <span>Right</span>
                      </label>
                    </div>
                  </div>

                  {/* Footer Text */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Footer Text
                    </label>
                    <input
                      type="text"
                      value={criteriaForm.footerText}
                      onChange={(e) => setCriteriaForm({...criteriaForm, footerText: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg"
                      placeholder="e.g., End of Examination Paper"
                    />
                    <div className="flex items-center space-x-4 mt-2">
                      <label className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                        <input 
                          type="radio" 
                          checked={criteriaForm.footerAlignment === 'left'}
                          onChange={() => setCriteriaForm({...criteriaForm, footerAlignment: 'left'})}
                          className="text-primary-600"
                        />
                        <SafeIcon icon={FiAlignLeft} />
                        <span>Left</span>
                      </label>
                      <label className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                        <input 
                          type="radio" 
                          checked={criteriaForm.footerAlignment === 'center'}
                          onChange={() => setCriteriaForm({...criteriaForm, footerAlignment: 'center'})}
                          className="text-primary-600"
                        />
                        <SafeIcon icon={FiAlignCenter} />
                        <span>Center</span>
                      </label>
                      <label className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                        <input 
                          type="radio" 
                          checked={criteriaForm.footerAlignment === 'right'}
                          onChange={() => setCriteriaForm({...criteriaForm, footerAlignment: 'right'})}
                          className="text-primary-600"
                        />
                        <SafeIcon icon={FiAlignRight} />
                        <span>Right</span>
                      </label>
                    </div>
                  </div>

                  {/* Page Numbers */}
                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      <input
                        type="checkbox"
                        checked={criteriaForm.showPageNumbers}
                        onChange={(e) => setCriteriaForm({...criteriaForm, showPageNumbers: e.target.checked})}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Show Page Numbers</span>
                    </label>
                    
                    {criteriaForm.showPageNumbers && (
                      <div className="grid grid-cols-3 gap-2">
                        <label className={`flex flex-col items-center border rounded-lg p-2 cursor-pointer ${
                          criteriaForm.pageNumberPosition === 'top-left' 
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          <input
                            type="radio"
                            className="hidden"
                            checked={criteriaForm.pageNumberPosition === 'top-left'}
                            onChange={() => setCriteriaForm({...criteriaForm, pageNumberPosition: 'top-left'})}
                          />
                          <div className="w-full h-10 relative">
                            <div className="absolute top-0 left-0 w-2 h-2 bg-gray-400 rounded-full"></div>
                          </div>
                          <span className="text-xs text-gray-600 dark:text-gray-400 mt-1">Top Left</span>
                        </label>
                        
                        <label className={`flex flex-col items-center border rounded-lg p-2 cursor-pointer ${
                          criteriaForm.pageNumberPosition === 'top-center' 
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          <input
                            type="radio"
                            className="hidden"
                            checked={criteriaForm.pageNumberPosition === 'top-center'}
                            onChange={() => setCriteriaForm({...criteriaForm, pageNumberPosition: 'top-center'})}
                          />
                          <div className="w-full h-10 relative">
                            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-400 rounded-full"></div>
                          </div>
                          <span className="text-xs text-gray-600 dark:text-gray-400 mt-1">Top Center</span>
                        </label>
                        
                        <label className={`flex flex-col items-center border rounded-lg p-2 cursor-pointer ${
                          criteriaForm.pageNumberPosition === 'top-right' 
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          <input
                            type="radio"
                            className="hidden"
                            checked={criteriaForm.pageNumberPosition === 'top-right'}
                            onChange={() => setCriteriaForm({...criteriaForm, pageNumberPosition: 'top-right'})}
                          />
                          <div className="w-full h-10 relative">
                            <div className="absolute top-0 right-0 w-2 h-2 bg-gray-400 rounded-full"></div>
                          </div>
                          <span className="text-xs text-gray-600 dark:text-gray-400 mt-1">Top Right</span>
                        </label>
                        
                        <label className={`flex flex-col items-center border rounded-lg p-2 cursor-pointer ${
                          criteriaForm.pageNumberPosition === 'bottom-left' 
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          <input
                            type="radio"
                            className="hidden"
                            checked={criteriaForm.pageNumberPosition === 'bottom-left'}
                            onChange={() => setCriteriaForm({...criteriaForm, pageNumberPosition: 'bottom-left'})}
                          />
                          <div className="w-full h-10 relative">
                            <div className="absolute bottom-0 left-0 w-2 h-2 bg-gray-400 rounded-full"></div>
                          </div>
                          <span className="text-xs text-gray-600 dark:text-gray-400 mt-1">Bottom Left</span>
                        </label>
                        
                        <label className={`flex flex-col items-center border rounded-lg p-2 cursor-pointer ${
                          criteriaForm.pageNumberPosition === 'bottom-center' 
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          <input
                            type="radio"
                            className="hidden"
                            checked={criteriaForm.pageNumberPosition === 'bottom-center'}
                            onChange={() => setCriteriaForm({...criteriaForm, pageNumberPosition: 'bottom-center'})}
                          />
                          <div className="w-full h-10 relative">
                            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-400 rounded-full"></div>
                          </div>
                          <span className="text-xs text-gray-600 dark:text-gray-400 mt-1">Bottom Center</span>
                        </label>
                        
                        <label className={`flex flex-col items-center border rounded-lg p-2 cursor-pointer ${
                          criteriaForm.pageNumberPosition === 'bottom-right' 
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          <input
                            type="radio"
                            className="hidden"
                            checked={criteriaForm.pageNumberPosition === 'bottom-right'}
                            onChange={() => setCriteriaForm({...criteriaForm, pageNumberPosition: 'bottom-right'})}
                          />
                          <div className="w-full h-10 relative">
                            <div className="absolute bottom-0 right-0 w-2 h-2 bg-gray-400 rounded-full"></div>
                          </div>
                          <span className="text-xs text-gray-600 dark:text-gray-400 mt-1">Bottom Right</span>
                        </label>
                      </div>
                    )}
                  </div>
                  
                  {/* Font Size */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Font Size
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <label className={`flex items-center justify-center border rounded-lg p-2 cursor-pointer ${
                        criteriaForm.fontSize === 'small' 
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        <input
                          type="radio"
                          className="hidden"
                          checked={criteriaForm.fontSize === 'small'}
                          onChange={() => setCriteriaForm({...criteriaForm, fontSize: 'small'})}
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Small</span>
                      </label>
                      
                      <label className={`flex items-center justify-center border rounded-lg p-2 cursor-pointer ${
                        criteriaForm.fontSize === 'medium' 
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        <input
                          type="radio"
                          className="hidden"
                          checked={criteriaForm.fontSize === 'medium'}
                          onChange={() => setCriteriaForm({...criteriaForm, fontSize: 'medium'})}
                        />
                        <span className="text-base text-gray-600 dark:text-gray-400">Medium</span>
                      </label>
                      
                      <label className={`flex items-center justify-center border rounded-lg p-2 cursor-pointer ${
                        criteriaForm.fontSize === 'large' 
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        <input
                          type="radio"
                          className="hidden"
                          checked={criteriaForm.fontSize === 'large'}
                          onChange={() => setCriteriaForm({...criteriaForm, fontSize: 'large'})}
                        />
                        <span className="text-lg text-gray-600 dark:text-gray-400">Large</span>
                      </label>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Generate Button */}
            <div className="flex justify-center pt-6">
              <motion.button
                type="button"
                onClick={generatePaperByCriteria}
                disabled={isLoading}
                className="px-8 py-3 bg-primary-600 dark:bg-primary-600 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isLoading ? (
                  <>
                    <SafeIcon icon={FiRefreshCw} className="animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiFileText} />
                    <span>Generate Paper</span>
                  </>
                )}
              </motion.button>
            </div>
          </form>
        </div>
        
        {/* QuickGen Modal */}
        <QuickGen 
          isOpen={showQuickGenModal}
          onClose={() => setShowQuickGenModal(false)}
          onGenerate={handleQuickGenSubmit}
        />
      </div>
    </div>
  );
};

export default PaperGenerator;